import { getAuthApiService } from './authApi'
import { emitSessionExpired } from './sessionEvents'
import { resolveApiBaseUrl } from '../config/apiBaseUrl'
import type { WorkspaceSettings } from '../types/whiteLabel'

// Base API configuration
// Prefer same-origin by default (works with Vite proxy and avoids mixed-content under HTTPS).
const API_BASE_URL = resolveApiBaseUrl()

// Standard response types
export interface ApiResponse<T> {
  success: true
  data: T
  meta?: {
    pagination?: PaginationMeta
    timestamp: string
    requestId: string
  }
}

export interface ApiError {
  success: false
  error: {
    code: string
    message: string
    details?: any
    timestamp: string
    requestId: string
  }
}

export interface PaginationMeta {
  page: number
  limit: number
  total: number
  totalPages: number
  hasNext: boolean
  hasPrev: boolean
}

// Entity types
export interface ProjectTemplate {
  id: string
  name: string
  displayName: string
  description: string
  theme: string
  categoryDisplayName?: string
  complexity?: string
  modernizationPattern?: string
  estimatedDuration?: string
  tags?: string[]
  sourceTechnology: string
  targetTechnology: string
  sourceStack?: Record<string, any>
  targetStack?: Record<string, any>
  icon: string
  colorScheme: string
  workflowPhases?: WorkflowPhase[]
  totalChapters?: number
  prerequisites?: string[]
  contextRequirements?: ContextRequirement[]
  configurationSchema?: Record<string, any>
  complianceStandards?: string[]
  features: string[]
  defaultAIModel?: string
  isActive: boolean
  isSystem: boolean
  version?: string
  createdAt?: string
  updatedAt?: string
}

export interface ModernizationPattern {
  pattern: string
  displayName: string
  description: string
  complexity: string
  estimatedDuration: string
  totalChapters: number
  icon: string
  colorScheme: string
}

export interface TechnologyStack {
  languages: string[]
  frameworks?: string[]
  platform: string
  database: string
  architecture: string
  version?: string
}

export interface WorkflowPhase {
  chapterNumber: number
  chapterName: string
  chapterDescription: string
  chapterType: string
  requiredArtifacts: string[]
  humanReviewRequired: boolean
  agentMode: boolean
}

export interface ContextRequirement {
  category: string
  field: string
  displayName: string
  description: string
  dataType: string
  required: boolean
}

export interface Workspace {
  id: string
  name: string
  description?: string
  clientOrganization?: string
  organizationId?: string
  settings?: WorkspaceSettings
  owner: {
    id: string
    email: string
    displayName: string
  }
  status: string
  memberCount: number
  projectCount: number
  completedProjects: number
  progressPercentage: number
  createdAt: string
  updatedAt: string
}

export interface Project {
  id: string
  name: string
  description?: string
  modulePath?: string
  currentStage: string
  progressPercentage: number
  status: string
  assignedTo?: {
    id: string
    email: string
    displayName: string
  }
  sourceFilesCount: number
  estimatedCompletion?: string
  startedAt?: string
  completedAt?: string
  createdAt: string
  updatedAt: string
}

export interface WorkspaceMember {
  id: string
  userId?: string
  user: {
    id: string
    email: string
    displayName: string
    avatarUrl?: string
  }
  role: string
  accessRole?: 'ADMIN' | 'LEAD' | 'CONTRIBUTOR' | 'REVIEWER' | 'VIEWER' | null
  effectiveAccessRole?: 'ADMIN' | 'LEAD' | 'CONTRIBUTOR' | 'REVIEWER' | 'VIEWER'
  persona?: 'SPONSOR' | 'DELIVERY_LEAD' | 'ARCHITECT' | 'ENGINEER' | 'VALIDATOR' | 'BUSINESS_GOVERNANCE' | null
  specialtyTags?: string[]
  responsibilitySummary?: string | null
  phaseScope?: string | null
  permissions: Record<string, any>
  joinedAt: string
}

export interface WorkspaceInvitation {
  id: string
  email: string
  accessRole: 'ADMIN' | 'LEAD' | 'CONTRIBUTOR' | 'REVIEWER' | 'VIEWER'
  persona?: 'SPONSOR' | 'DELIVERY_LEAD' | 'ARCHITECT' | 'ENGINEER' | 'VALIDATOR' | 'BUSINESS_GOVERNANCE' | null
  specialtyTags?: string[]
  responsibilitySummary?: string | null
  phaseScope?: string | null
  status: string
  createdAt: string
  expiresAt: string
  inviter?: {
    id: string
    email: string
    displayName: string
  }
  /** Accept link for PENDING invitations — only returned to ADMIN/LEAD callers. */
  acceptUrl?: string
  /** Whether the invitation email actually went out (create/resend responses). */
  emailSent?: boolean
  /** Whether SMTP delivery is configured on this deployment (list responses). */
  emailConfigured?: boolean
}

/**
 * Main API Service Class
 * Handles all backend API calls with proper authentication
 */
export class ApiService {
  private baseUrl: string

  constructor() {
    // Normalize so env can be either "https://host" or "https://host/api"
    const normalized = API_BASE_URL.endsWith('/api')
      ? API_BASE_URL
      : (API_BASE_URL ? `${API_BASE_URL}/api` : '/api')
    this.baseUrl = normalized
  }

  /**
   * Make authenticated API request
   */
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    try {
      const authService = getAuthApiService()
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      }

      const authHeaders = await authService.getAuthHeader()
      Object.assign(headers, authHeaders)

      const fetchInit: RequestInit = {
        ...options,
        headers: {
          ...headers,
          ...options.headers,
        },
      }
      const fullUrl = `${this.baseUrl}${endpoint}`
      let response = await fetch(fullUrl, fetchInit)

      // Single 401 refresh-and-retry (hard loop guard — at most once per call).
      if (response.status === 401) {
        const newToken = await authService.refreshSessionSilently()
        if (newToken) {
          const retryHeaders = new Headers(fetchInit.headers)
          retryHeaders.set('Authorization', `Bearer ${newToken}`)
          response = await fetch(fullUrl, { ...fetchInit, headers: retryHeaders })
        }
        if (!newToken || response.status === 401) {
          emitSessionExpired()
          throw new Error('Authentication required')
        }
      }

      // Some upstream failures (proxy, load balancer) may return non-JSON or an empty body.
      // Read text first and parse JSON only when safe.
      const contentType = response.headers.get('content-type') || ''
      const rawText = await response.text()
      const hasJsonBody = contentType.includes('application/json') && rawText.trim().length > 0
      const data = hasJsonBody ? JSON.parse(rawText) : (rawText ? { message: rawText } : {})

      if (!response.ok) {
        // Handle authentication errors (will only reach here for non-401 failures now)
        if (response.status === 401) {
          throw new Error('Authentication required')
        }

        // Log full error details for debugging
        console.error(`❌ API Error (${response.status}):`, {
          endpoint,
          status: response.status,
          statusText: response.statusText,
          errorData: data
        })

        throw new Error((typeof data.error === 'string' ? data.error : data.error?.message) || data.message || `HTTP ${response.status}`)
      }

      // If we got a 2xx but couldn't parse JSON, fail loudly (the frontend expects ApiResponse<T>)
      if (!hasJsonBody) {
        throw new Error(`Invalid API response (expected JSON) for ${endpoint}: ${rawText || '<empty body>'}`)
      }

      return data as ApiResponse<T>
    } catch (error) {
      console.error(`API request failed: ${endpoint}`, error)
      throw error
    }
  }

  // ==========================================
  // HTTP METHODS (for compatibility with storybookService)
  // ==========================================

  /**
   * GET request
   */
  async get<T = any>(endpoint: string): Promise<{ data: T }> {
    const response = await this.request<T>(endpoint)
    return { data: response.data }
  }

  /**
   * POST request
   */
  async post<T = any>(endpoint: string, data?: any): Promise<{ data: T }> {
    const response = await this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    })
    return { data: response.data }
  }

  /**
   * POST request that returns the full API response (including columns, rowCount, etc.)
   * Use this when you need access to additional fields beyond just the data array
   */
  async postFull<T = any>(endpoint: string, data?: any): Promise<{ data: T }> {
    const response = await this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    })
    // Return the full response object, not just response.data
    // This preserves additional fields like columns, rowCount, executionTimeMs
    return { data: response as unknown as T }
  }

  /**
   * POST request with FormData (for file uploads)
   */
  async postFormData<T = any>(endpoint: string, formData: FormData): Promise<{ data: T }> {
    const url = `${this.baseUrl}${endpoint}`
    const authApi = getAuthApiService()

    // Get auth headers (uses Azure AD token if available, falls back to stored JWT)
    const authHeaders = await authApi.getAuthHeader()

    const headers: Record<string, string> = {
      ...authHeaders,
    }
    // Note: Don't set Content-Type for FormData - browser will set it with boundary

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
    }

    const data = await response.json()
    return { data: data as T }
  }

  /**
   * PUT request
   */
  async put<T = any>(endpoint: string, data?: any): Promise<{ data: T }> {
    const response = await this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    })
    return { data: response.data }
  }

  /**
   * PATCH request
   */
  async patch<T = any>(endpoint: string, data?: any): Promise<{ data: T }> {
    const response = await this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    })
    return { data: response.data }
  }

  /**
   * DELETE request
   */
  async delete<T = any>(endpoint: string): Promise<{ data: T }> {
    const response = await this.request<T>(endpoint, {
      method: 'DELETE',
    })
    return { data: response.data }
  }

  // ==========================================
  // PROJECT TEMPLATES
  // ==========================================

  /**
   * Get all project templates
   */
  async getProjectTemplates(params?: {
    sourceType?: string
    targetType?: string
    active?: boolean
  }): Promise<ProjectTemplate[]> {
    const queryParams = new URLSearchParams()
    
    if (params?.sourceType) queryParams.append('sourceType', params.sourceType)
    if (params?.targetType) queryParams.append('targetType', params.targetType)
    if (params?.active !== undefined) queryParams.append('active', params.active.toString())

    const query = queryParams.toString()
    const endpoint = `/templates${query ? `?${query}` : ''}`
    
    const response = await this.request<ProjectTemplate[]>(endpoint)
    return response.data
  }

  /**
   * Get specific template details
   */
  async getProjectTemplate(id: string): Promise<ProjectTemplate> {
    const response = await this.request<ProjectTemplate>(`/templates/${id}`)
    return response.data
  }

  /**
   * Get available modernization patterns
   */
  async getModernizationPatterns(): Promise<ModernizationPattern[]> {
    const response = await this.request<ModernizationPattern[]>('/templates/meta/patterns')
    return response.data
  }

  /**
   * Get templates by modernization pattern
   */
  async getTemplatesByPattern(pattern: string): Promise<ProjectTemplate[]> {
    const response = await this.request<ProjectTemplate[]>(`/templates/patterns/${pattern}`)
    return response.data
  }

  // ==========================================
  // WORKSPACES
  // ==========================================

  /**
   * Get user's workspaces
   */
  async getWorkspaces(params?: {
    page?: number
    limit?: number
    status?: string
    search?: string
    sortBy?: string
    sortOrder?: 'asc' | 'desc'
  }): Promise<{ workspaces: Workspace[]; pagination: PaginationMeta }> {
    const queryParams = new URLSearchParams()

    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.status) queryParams.append('status', params.status)
    if (params?.search) queryParams.append('search', params.search)
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy)
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder)

    const query = queryParams.toString()
    const endpoint = `/workspaces${query ? `?${query}` : ''}`

    const result = await this.request<Workspace[]>(endpoint)
    const pagination = result.meta?.pagination
    const page = params?.page ?? 1
    const limit = params?.limit ?? 10
    if (result.success !== true || !Array.isArray(result.data) || !pagination ||
      pagination.page !== page || pagination.limit !== limit ||
      !Number.isInteger(pagination.total) || pagination.total < 0 ||
      pagination.totalPages !== Math.ceil(pagination.total / limit) ||
      pagination.hasNext !== (page < pagination.totalPages) || pagination.hasPrev !== (page > 1) ||
      result.data.length > limit ||
      result.data.some(workspace => !workspace || typeof workspace.id !== 'string') ||
      new Set(result.data.map(workspace => workspace.id)).size !== result.data.length) {
      throw new Error('The Program page could not be verified. Retry the read.')
    }
    return {
      workspaces: result.data,
      pagination,
    }
  }

  /**
   * Create new workspace
   */
  async createWorkspace(data: {
    name: string
    description?: string
    clientOrganization?: string
    organizationId?: string
    settings?: Record<string, any>
  }): Promise<Workspace> {
    const result = await this.request<Workspace>('/workspaces', {
      method: 'POST',
      body: JSON.stringify(data),
    })
    return result.data
  }

  /**
   * Get workspace details
   */
  async getWorkspace(id: string): Promise<Workspace> {
    const response = await this.request<Workspace>(`/workspaces/${encodeURIComponent(id)}`)
    if (response.success !== true || !response.data || response.data.id !== id) {
      throw new Error('The requested Program identity could not be verified.')
    }
    return response.data
  }

  /**
   * Update workspace
   */
  async updateWorkspace(id: string, data: {
    name?: string
    description?: string
    clientOrganization?: string
    status?: string
    settings?: Record<string, any>
  }): Promise<Workspace> {
    const response = await this.request<Workspace>(`/workspaces/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
    return response.data
  }

  /**
   * Delete workspace (archive)
   */
  async deleteWorkspace(id: string): Promise<void> {
    await this.request(`/workspaces/${id}`, {
      method: 'DELETE',
    })
  }

  async permanentlyDeleteWorkspace(id: string): Promise<void> {
    const response = await this.request(`/workspaces/${id}/permanent`, { method: 'DELETE' })
    if (response.success !== true) throw new Error('Unable to delete this program. Please try again.')
  }

  // ==========================================
  // WORKSPACE MEMBERS
  // ==========================================

  /**
   * Get workspace members
   */
  async getWorkspaceMembers(workspaceId: string): Promise<WorkspaceMember[]> {
    const response = await this.request<WorkspaceMember[]>(`/workspaces/${workspaceId}/members`)
    return response.data
  }

  /**
   * Add member to workspace
   */
  async addWorkspaceMember(workspaceId: string, data: {
    userId: string
    role: 'ADMIN' | 'MEMBER' | 'VIEWER'
    accessRole?: 'ADMIN' | 'LEAD' | 'CONTRIBUTOR' | 'REVIEWER' | 'VIEWER'
    persona?: 'SPONSOR' | 'DELIVERY_LEAD' | 'ARCHITECT' | 'ENGINEER' | 'VALIDATOR' | 'BUSINESS_GOVERNANCE' | null
    specialtyTags?: string[]
    responsibilitySummary?: string
    phaseScope?: string
    profileMetadata?: Record<string, any>
    permissions?: Record<string, any>
  }): Promise<WorkspaceMember> {
    const response = await this.request<WorkspaceMember>(`/workspaces/${workspaceId}/members`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
    return response.data
  }

  /**
   * Update member role/permissions
   */
  async updateWorkspaceMember(workspaceId: string, userId: string, data: {
    accessRole?: 'ADMIN' | 'LEAD' | 'CONTRIBUTOR' | 'REVIEWER' | 'VIEWER'
    persona?: 'SPONSOR' | 'DELIVERY_LEAD' | 'ARCHITECT' | 'ENGINEER' | 'VALIDATOR' | 'BUSINESS_GOVERNANCE' | null
    specialtyTags?: string[]
    responsibilitySummary?: string | null
    phaseScope?: string | null
    profileMetadata?: Record<string, any>
    permissions?: Record<string, any>
  }): Promise<WorkspaceMember> {
    const response = await this.request<WorkspaceMember>(`/workspaces/${workspaceId}/members/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
    return response.data
  }

  /**
   * Remove member from workspace
   */
  async removeWorkspaceMember(workspaceId: string, userId: string): Promise<void> {
    await this.request(`/workspaces/${workspaceId}/members/${userId}`, {
      method: 'DELETE',
    })
  }

  async listWorkspaceInvitations(workspaceId: string): Promise<WorkspaceInvitation[]> {
    const response = await this.request<WorkspaceInvitation[]>(`/workspaces/${workspaceId}/invitations`)
    return response.data
  }

  async resendWorkspaceInvitation(workspaceId: string, invitationId: string): Promise<WorkspaceInvitation> {
    const response = await this.request<WorkspaceInvitation>(
      `/workspaces/${workspaceId}/invitations/${invitationId}/resend`,
      { method: 'POST' }
    )
    return response.data
  }

  async revokeWorkspaceInvitation(workspaceId: string, invitationId: string): Promise<void> {
    await this.request(`/workspaces/${workspaceId}/invitations/${invitationId}`, {
      method: 'DELETE',
    })
  }

  async inviteWorkspaceMember(workspaceId: string, data: {
    email: string
    accessRole: 'ADMIN' | 'LEAD' | 'CONTRIBUTOR' | 'REVIEWER' | 'VIEWER'
    persona?: 'SPONSOR' | 'DELIVERY_LEAD' | 'ARCHITECT' | 'ENGINEER' | 'VALIDATOR' | 'BUSINESS_GOVERNANCE' | null
    specialtyTags?: string[]
    responsibilitySummary?: string
    phaseScope?: string
    message?: string
  }): Promise<WorkspaceInvitation> {
    const response = await this.request<WorkspaceInvitation>(`/workspaces/${workspaceId}/invitations`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
    return response.data
  }

  // ==========================================
  // PROJECTS
  // ==========================================

  /**
   * Get workspace projects
   */
  async getProjects(workspaceId: string, params?: {
    page?: number
    limit?: number
    status?: string
    assignedTo?: string
    stage?: string
    sortBy?: string
    sortOrder?: 'asc' | 'desc'
  }): Promise<{ projects: Project[]; pagination: PaginationMeta }> {
    const queryParams = new URLSearchParams()
    
    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.status) queryParams.append('status', params.status)
    if (params?.assignedTo) queryParams.append('assignedTo', params.assignedTo)
    if (params?.stage) queryParams.append('stage', params.stage)
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy)
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder)

    const query = queryParams.toString()
    const endpoint = `/workspaces/${workspaceId}/projects${query ? `?${query}` : ''}`
    
    const response = await this.request<Project[]>(endpoint)
    return {
      projects: response.data,
      pagination: response.meta?.pagination || {
        page: 1,
        limit: 10,
        total: response.data.length,
        totalPages: 1,
        hasNext: false,
        hasPrev: false,
      },
    }
  }

  /**
   * Create new project
   */
  async createProject(workspaceId: string, data: {
    name: string
    description?: string
    modulePath?: string
    assignedTo?: string
    estimatedCompletion?: string
  }): Promise<Project> {
    const response = await this.request<Project>(`/workspaces/${workspaceId}/projects`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
    return response.data
  }

  /**
   * Get project details
   */
  async getProject(id: string): Promise<Project> {
    const response = await this.request<Project>(`/projects/${id}`)
    return response.data
  }

  /**
   * Update project
   */
  async updateProject(id: string, data: {
    name?: string
    description?: string
    status?: string
    assignedTo?: string
    currentStage?: string
    progressPercentage?: number
  }): Promise<Project> {
    const response = await this.request<Project>(`/projects/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
    return response.data
  }

  // ==========================================
  // DASHBOARD & ANALYTICS
  // ==========================================

  /**
   * Get dashboard statistics
   */
  async getDashboardStats(): Promise<{
    activeWorkspaces: number
    projectsCompleted: number
    linesMigrated: number
    successRate: number
    recentActivity: any[]
  }> {
    const response = await this.request<any>('/dashboard/stats')
    return response.data
  }

  /**
   * Get dashboard workspaces (recent workspaces for dashboard)
   */
  async getDashboardWorkspaces(limit: number = 6): Promise<Workspace[]> {
    const response = await this.request<Workspace[]>(`/dashboard/workspaces?limit=${limit}`)
    return response.data
  }

  /**
   * Get workspace analytics
   */
  async getWorkspaceAnalytics(workspaceId: string, params?: {
    startDate?: string
    endDate?: string
    metrics?: string[]
  }): Promise<any> {
    const queryParams = new URLSearchParams()
    
    if (params?.startDate) queryParams.append('startDate', params.startDate)
    if (params?.endDate) queryParams.append('endDate', params.endDate)
    if (params?.metrics) params.metrics.forEach(m => queryParams.append('metrics', m))

    const query = queryParams.toString()
    const endpoint = `/workspaces/${workspaceId}/analytics${query ? `?${query}` : ''}`
    
    const response = await this.request<any>(endpoint)
    return response.data
  }
}

// Create singleton instance
export const apiService = new ApiService()
export default apiService
